import {
  Deployment,
  formatBytecodeVersion,
  shortenHash,
} from "@gearbox-protocol/sdk/permissionless";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CopyButton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  ExternalButton,
  formatBytecodeSize,
  formatTimestamp,
  Label,
  PageLayout,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TabButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@gearbox-protocol/ui-kit";
import { ExternalLink, ExternalLinkIcon, History } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { hexToString } from "viem";
import { AddAuditDialog } from "@/components/bytecode/dialog-add-audit";
import { BytecodeInfo, BytecodeShort } from "@/core/bytecode";
import { Instance } from "@/core/instance";
import { useAddAudit } from "@/hooks/use-bytecode-add-audit";

interface BytecodeRepositoryDetailsViewProps {
  bytecode: BytecodeInfo;
  listBytecodes: BytecodeShort[];
  instances: Instance[];
}

function parseContractType(contractType: string) {
  return hexToString(contractType as `0x${string}`, { size: 32 });
}

export function BytecodeRepositoryDetailsView({
  instances,
  listBytecodes,
  bytecode,
}: BytecodeRepositoryDetailsViewProps) {
  const [isAddAuditDialogOpen, setIsAddAuditDialogOpen] = useState(false);
  const { canSign } = useAddAudit({ onSuccess: () => {} });

  const otherVersions = useMemo(() => {
    return listBytecodes
      .filter(
        bc =>
          bc.contractType === bytecode.contractType &&
          bc.bytecodeHash !== bytecode.bytecodeHash,
      )
      .sort((a, b) => {
        if (a.auditorNames.length === 0 && b.auditorNames.length > 0) {
          return 1;
        }
        return a.version - b.version;
      });
  }, [listBytecodes, bytecode]);

  const mainnetInstance = useMemo(() => {
    return instances.find(inst => inst.chainId === 1);
  }, [instances]);

  const deploymentsByChain = useMemo(() => {
    return (
      bytecode.deployments.reduce<Record<number, Deployment[]>>(
        (acc, deployment) => {
          const chainId = deployment.chainId;
          if (!acc[chainId]) {
            acc[chainId] = [];
          }
          acc[chainId].push(deployment);
          return acc;
        },
        {},
      ) || {}
    );
  }, [bytecode.deployments]);

  const availableChainIds = useMemo(() => {
    return Object.keys(deploymentsByChain).map(id => +id);
  }, [deploymentsByChain]);

  const [selectedChainId, setSelectedChainId] = useState<string>("");

  useEffect(() => {
    if (availableChainIds.length > 0 && !selectedChainId) {
      setSelectedChainId(availableChainIds[0].toString());
    }
  }, [availableChainIds, selectedChainId]);

  return (
    <PageLayout
      title={
        bytecode.contractType ? parseContractType(bytecode.contractType) : ""
      }
      backButton={{
        text: "Back to bytecodes",
        href: "/",
      }}
      actionButton={
        <a href={bytecode.source} target="_blank" rel="noopener noreferrer">
          <Button className="flex items-center gap-2">
            Source Code
            <ExternalLink className="h-4 w-4" />
          </Button>
        </a>
      }
      description={
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <span>Uploaded by {shortenHash(bytecode.author)}</span>
          <div className="flex items-center">
            <CopyButton text={bytecode.author} />
            {mainnetInstance && (
              <ExternalButton
                url={`${mainnetInstance.explorerUrl}address/${bytecode.author}`}
              />
            )}
          </div>
          <span>at {formatTimestamp(bytecode.uploadedAt)}</span>
        </div>
      }
    >
      <div className="space-y-6">
        <Card variant="transparent">
          <CardHeader className="gap-8 justify-between items-start">
            <div>
              <CardTitle>Hash:</CardTitle>
              <div className="flex items-center gap-1">
                <span className="font-mono">
                  {shortenHash(bytecode.bytecodeHash)}
                </span>
                <CopyButton text={bytecode.bytecodeHash} name="Hash" />
              </div>
            </div>

            <div className="flex flex-col items-end">
              <CardTitle className="w-fit">
                {formatBytecodeVersion(bytecode.version)}
              </CardTitle>
              {otherVersions.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <Button
                      variant="text"
                      size="auto"
                      className="flex items-center gap-2 p-0"
                    >
                      History
                      <History className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuContent>
                      <DropdownMenuLabel>Other versions:</DropdownMenuLabel>
                      {otherVersions.map(bc => (
                        <Link
                          key={bc.bytecodeHash}
                          to={`/hash/${bc.bytecodeHash}`}
                        >
                          <DropdownMenuItem className="flex w-full justify-end cursor-pointer">
                            <div
                              className={`flex items-center gap-2 ${bc.auditorNames.length > 0 ? "" : "text-destructive"}`}
                            >
                              {formatBytecodeVersion(bc.version)}
                              <ExternalLinkIcon className="h-3.5 w-3.5" />
                            </div>
                          </DropdownMenuItem>
                        </Link>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenuPortal>
                </DropdownMenu>
              )}
            </div>
          </CardHeader>
          <CardContent
            className={bytecode.isAllowed ? undefined : "text-destructive"}
          >
            {`Bytecode is ${
              bytecode.isAllowed
                ? `allowed using ${bytecode.domain} domain`
                : "not allowed as either system or public-domain"
            }`}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_400px] gap-6">
          {/* Source Code */}
          <Card variant="transparent">
            <CardHeader className="justify-between">
              <div className="flex items-center gap-2">
                <CardTitle>Bytecode</CardTitle>
                <CardDescription>
                  (Size: {formatBytecodeSize(bytecode.size)})
                </CardDescription>
              </div>
              <CopyButton text={bytecode.initCode} name="Bytecode" />
            </CardHeader>
            <CardContent className="font-mono text-sm overflow-x-auto overflow-y-auto max-h-[300px] md:max-h-[400px]">
              <pre className="relative">
                <code className="grid whitespace-pre-wrap break-all">
                  {bytecode.initCode}
                </code>
              </pre>
            </CardContent>
          </Card>

          {/* Audit Reports */}
          <Card variant="transparent">
            <CardHeader className="justify-between">
              <CardTitle>Audit Reports</CardTitle>
              <TabButton
                variant="outline"
                size="sm"
                onClick={() => setIsAddAuditDialogOpen(true)}
                disabled={!canSign}
              >
                Add Audit
              </TabButton>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {bytecode.audits.length > 0 ? (
                bytecode.audits.map(audit => (
                  <div
                    key={`${audit.auditor}-${audit.report}`}
                    className="flex gap-4 justify-between items-center"
                  >
                    <div className="group flex items-center gap-2">
                      <span className="font-medium">{audit.auditorName}</span>

                      <div className="flex gap-2 opacity-0 transition-opacity group-hover:opacity-100 items-center">
                        <span className="text-muted-foreground text-sm font-mono">
                          {shortenHash(audit.auditor)}
                        </span>
                        <CopyButton text={audit.auditor} />
                      </div>
                    </div>

                    <a
                      href={audit.report}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="text"
                        size="auto"
                        className="flex items-center gap-1"
                      >
                        Report
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                  </div>
                ))
              ) : (
                <p className="text-destructive">No audits available</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Deployed Contracts */}
        <div className="space-y-4">
          <CardTitle>Deployed Contracts</CardTitle>
          {availableChainIds.length === 0 ? (
            <p className="text-muted-foreground">
              There have been no deployments
            </p>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <Label className="text-md font-semibold mb-0">Chain:</Label>
                <Select
                  value={selectedChainId}
                  onValueChange={setSelectedChainId}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select chain" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableChainIds.map(chainId => {
                      const instance = instances.find(
                        inst => inst.chainId === chainId,
                      );
                      return (
                        <SelectItem key={chainId} value={chainId.toString()}>
                          {instance?.name ?? `Chain ${chainId}`}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              {selectedChainId && deploymentsByChain[+selectedChainId] && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Address</TableHead>
                        <TableHead>Transaction Hash</TableHead>
                        <TableHead>Block</TableHead>
                        <TableHead className="text-right">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deploymentsByChain[+selectedChainId].map(deployment => {
                        const instance = instances.find(
                          inst => inst.chainId === +selectedChainId,
                        );
                        return (
                          <TableRow
                            key={`${selectedChainId}-${deployment.txHash}`}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="font-mono">
                                  {deployment.address}
                                </span>
                                <div className="flex items-center">
                                  <CopyButton text={deployment.address} />
                                  {instance && (
                                    <ExternalButton
                                      url={`${instance.explorerUrl}address/${deployment.address}`}
                                    />
                                  )}
                                </div>
                              </div>
                            </TableCell>

                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="font-mono">
                                  {shortenHash(deployment.txHash, 4)}
                                </span>
                                <div className="flex items-center">
                                  <CopyButton
                                    text={deployment.txHash}
                                    name="Tx Hash"
                                  />
                                  {instance && (
                                    <ExternalButton
                                      url={`${instance.explorerUrl}tx/${deployment.txHash}`}
                                    />
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span>{deployment.blockNumber}</span>
                                {instance && (
                                  <ExternalButton
                                    url={`${instance.explorerUrl}block/${deployment.blockNumber}`}
                                  />
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {formatTimestamp(deployment.timestamp)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {isAddAuditDialogOpen && (
        <AddAuditDialog
          onClose={() => setIsAddAuditDialogOpen(false)}
          bytecodeHash={bytecode.bytecodeHash}
        />
      )}
    </PageLayout>
  );
}
